import { getPoaIndex, getSelectedPeriod } from "../../../services/GroupContext";
import { processDateJS } from "../../../utils/dateProcessor";
import {
  createExcelDocument,
  saveExcelFile,
  addGroupCompaniesTableStyles,
  addValidations,
  parseExcelFileToJson,
  getWorksheetFromFile,
  getWorksheetHeaders,
  hideColumn,
  disableColumnEditing,
  protectWorksheet,
  filterNewRows,
  enableCellEditingByIndex,
  addInstructionsWorksheet,
} from "./DataImportUtils";

const templateWorksheetName = "UK group companies";

const headers = [
  {
    title: "UK Group companies - names",
    metricId: "company_name",
    display: "Company Name",
  },
  { title: "Tax Office", metricId: "tax_office" },
  { title: "UTR (10 digits)", metricId: "utr", display: "UTR" },
  { title: "Date joined (if during POA)", metricId: "date_join" },
  { title: "Date left (if during POA)", metricId: "date_left" },
  { title: "Disabled", metricId: "disabled" },
];

const columnsConfig = [
  { name: "A", type: "text" },
  { name: "B", type: "number", length: 3, subtype: "tax_office" },
  { name: "C", type: "number", length: 10, subtype: "utr" },
  { name: "D", type: "date" },
  { name: "E", type: "date" },
  { name: "F", type: "boolean" },
];

const instructionsSheetBuilder = [
  {
    value: "User Information and Guidance - Group Companies Data Import",
    type: "header",
  },
  { value: "" },
  { value: "Uploading / Downloading", type: "header" },
  {
    value:
      "This template allows the user to upload the details of the UK Group Companies included within the CIR Worldwide Group and is intended for use in the first period of account of the group or for periods in which there are substantial additions.",
  },
  {
    value:
      "Where a company has already been entered in the CIR Analyser, the information for this company will be downloaded and pre-populated in the template but cannot be edited.",
  },
  {
    value:
      "Once the template is complete and has been saved, return to the Group Companies page in the CIR Analyser, select Import/Export Template, and attach the template file before pressing Import Data. The contents of the template should be uploaded to the Group Companies page.",
  },
  {
    value:
      "Uploaded data will not be saved to the CIR Analyser until the Save, Submit, or Next button has been pressed.",
  },
  {
    value:
      'The CIR analyser locates the relevant sheet based on its name ("Group Companies"). Do not rename the sheet or the upload should fail.',
  },
  { value: "" },
  { value: "Entering Data", type: "header" },
  {
    value:
      "It is possible to complete the data entry using formulas linking to cells in other tabs added to this spreadsheet, or in other spreadsheets. ",
  },
  {
    value:
      "Grey cells are system generated and cannot be edited. New companies can be edited in the first 300 white rows. If you need to add more than 300 companies, you can complete multiple uploads.",
  },
  { value: "The names of the UK Group Companies should be added in Column A." },
  {
    value:
      "The Tax Office number of the company should be entered in Column B. Where the Tax Office number is not known, 454 can be used.",
  },
  {
    value:
      "The company UTR should be entered in Column C, in the format 11111-11111 or 1111111111.",
  },
  {
    value:
      "If the company has joined or left the group in the period, the join or leave dates can be entered in Columns D and E. Data should only be entered in these columns where a company is a joiner or leaver",
  },
  { value: "" },
  { value: "Data Validations", type: "header" },
  { value: "The following data validations exist within the spreadsheet:" },
  {
    value:
      "Column A - This must be a text entry (i.e. contains some letters or special characters). Entries that would fail data validation include numerical entries, dates and booleans (i.e. true / false).",
  },
  {
    value:
      "Column B - This must be a numerical entry that is three digits in length. It can contain a leading zero.",
  },
  {
    value:
      "Column C - This must be an entry in the form of a 10-digit number or an entry in the format xxxxx-xxxxx.",
  },
  {
    value:
      "Column D - This must be an entry in a date format. The validation within the spreadsheet accepts any date between 1/1/1900 and 31/12/9999.",
  },
  {
    value:
      "Column E - This must be an entry in a date format. The validation within the spreadsheet accepts any date between 1/1/1900 and 31/12/9999.",
  },
  { value: "" },
  { value: "Upload Warnings & Errors", type: "header" },
  {
    value:
      "On upload, the CIR Analyser may identify errors or warnings. Errors must be rectified within the spreadsheet and the spreadsheet must be re-uploaded. Warnings highlight potential issues which, if they need amending, may be amended within the Group Companies page of the CIR Analyser. When these may arise are outlined below:",
  },
  { value: "" },
  {
    value: "Errors (prevent uploading - must be corrected and re-uploaded):",
    styles: ["underline", "italic"],
  },
  {
    value:
      'Changes to Row Headers - An error is flagged if the name of the Headers (i.e. Row 1) is altered. The error states "Headers of imported file do not match with the headers specified for page or you have provided an empty file."',
  },
  {
    value:
      "Entries without a Company Name - An error is flagged if there is a row for which some data has been entered, but there is no company name.",
  },
  {
    value:
      'Company Name - An error is flagged if this is not a text entry (i.e. is not an entry that contains some letters or special characters). Entries that would fail data validation include numerical entries, dates and booleans (i.e. true / false). The error message will state "There are companies with invalid text names"',
  },
  {
    value:
      'Tax Office - An error is flagged if the tax office is not a number of exactly three digits long. The error message will state "Tax Office for the following companies must be a number of 3 digits:" and list the relevant companies.',
  },
  {
    value:
      'UTR - An error is flagged if the utr is not a number of exactly 10 digits long, or in the format XXXXX-XXXXX or XXXXXXXXXX. The error message will state "UTR for the following companies must be a number in the format XXXXX-XXXXX or XXXXXXXXXX:" and list the relevant companies.',
  },
  { value: "" },
  {
    value:
      "Warnings (do not prevent uploading, usual validations within the Group Companies page continue to apply before page can be saved):",
    styles: ["underline", "italic"],
  },
  {
    value:
      'Company Join Date Outside PoA - A warning is raised if a company is added with a join date that is outside the Period of Account. The warning message will state "The following companies have a join date that is outside the PoA:" and list the relevant companies.',
  },
  {
    value:
      'Company Join Date Before Company Leave Date - A warning is raised if a company is added with a join date that is before its leave date. The warning message will state "The following companies have a date left before date joined:" and list the relevant companies.',
  },
  {
    value:
      'Empty Tax Office Number - A warning is raised if a company is added without a tax office number. The warning message will state "The following companies have empty Tax Office values:" and list the relevant companies. If the company is not a UK Group Company, this warning can be ignored.',
  },
  {
    value:
      'Empty UTR - A warning is raised if a company is added without a UTR. The warning message will state "The following companies have empty UTR values:" and list the relevant companies. If the company is not a UK Group Company, this warning can be ignored.',
  },
  {
    value:
      'Empty Join Date - A warning is raised if a company is added without a Join Date, unless the company is added in the First Period of Account for which the model is used. The warning message will state "The following companies have empty Join Dates:" and list the relevant companies. If the company is not a UK Group Company, this warning can be ignored.',
  },
  { value: "" },
];

const addCompanyRows = (worksheet, data) => {
  const rows = data.map((row) => [
    row.company_name,
    row.tax_office,
    row.utr,
    row.date_join,
    row.data_left,
    true,
  ]);

  worksheet?.addRows(rows);
};

const transformCompanyFormat = (importedGroupCompaniesDTO) => {
  const dateColumns = [
    "Date joined (if during POA)",
    "Date left (if during POA)",
  ];
  const dashColumns = ["UTR (10 digits)"];

  const getModifiedValue = (company, key) => {
    if (dateColumns.includes(key)) return processDateJS(company[key]);
    else if (dashColumns.includes(key))
      return String(company[key])?.replaceAll("-", "");
    else return company[key];
  };

  let importedGroupCompanies = [];
  const titleToMetricMap = headers.reduce((acc, { title, metricId }) => {
    acc[title] = metricId;
    return acc;
  }, {});

  for (let company of importedGroupCompaniesDTO) {
    const newCompany = {};
    Object.keys(company).forEach((key) => {
      newCompany[titleToMetricMap[key]] = getModifiedValue(company, key);
    });
    newCompany["new"] = true;
    importedGroupCompanies.push(newCompany);
  }

  return importedGroupCompanies;
};

const isCompanyNameValid = (name) => {
  return name ? typeof name === "string" : false;
};

const isValueValid = (value, length, removeDash = false) => {
  const stringValue = removeDash
    ? String(value)?.replaceAll("-", "")
    : String(value);
  const regex = new RegExp(`^\\d{${length}}$`);

  return regex.test(stringValue);
};

const isDateValid = (value) => {
  return value && !isNaN(new Date(value).getTime());
};

const isTaxOfficeErrorFree = (value) => {
  return !value || isValueValid(value, 3);
};

const isUtrErrorFree = (value) => {
  return !value || isValueValid(value, 10, true);
};

const joinWithCommas = (list) => {
  return list.join(", ");
};

const validateHeaders = (importedHeaders, importedGroupCompaniesDTO) => {
  const titleHeaders = headers.map((header) => header.title);

  const hasEmptyImportedHeader = importedGroupCompaniesDTO.length === 0;
  const hasNonMatchingHeaders = importedHeaders.some(
    (header, index) => header !== titleHeaders[index],
  );

  if (hasEmptyImportedHeader || hasNonMatchingHeaders) {
    const errorMessages = [
      "Headers of imported file do not match with the headers specified for page or you have provided an empty file.",
    ];
    const error = new Error(
      JSON.stringify([{ title: null, messages: errorMessages }]),
    );
    error.type = "error";
    throw error;
  }
};

const getNonEmptyIssues = (issues) => {
  let nonEmptyIssues = [];

  nonEmptyIssues = issues
    .filter((issue) => issue.messages.length > 0)
    .map((issue) => ({
      ...issue,
      messages: [joinWithCommas(issue.messages)],
    }));

  return nonEmptyIssues;
};

const validateGroupCompanyRows = (groupCompanies) => {
  const invalidNameCompanies = [];
  const invalidTaxOfficeCompanies = [];
  const invalidUtrCompanies = [];

  groupCompanies.forEach((company) => {
    const companyName = company?.company_name;
    if (!isCompanyNameValid(companyName))
      invalidNameCompanies.push(companyName);

    if (!isTaxOfficeErrorFree(company?.tax_office))
      invalidTaxOfficeCompanies.push(companyName);

    if (!isUtrErrorFree(company?.utr)) invalidUtrCompanies.push(companyName);
  });

  const allCompaniesErrors = [
    {
      title: "There are companies with invalid text names",
      messages: invalidNameCompanies,
    },
    {
      title:
        "Tax Office for the following companies must be a number of 3 digits:",
      messages: invalidTaxOfficeCompanies,
    },
    {
      title:
        "UTR for the following companies must be a number in the format XXXXX-XXXXX or XXXXXXXXXX:",
      messages: invalidUtrCompanies,
    },
  ];

  const errors = getNonEmptyIssues(allCompaniesErrors);
  if (errors.length > 0) {
    const error = new Error(JSON.stringify(errors));
    error.type = "error";
    throw error;
  }
};

const checkCompanyForWarnings = (data, importedCompanies) => {
  const dateOutsidePoAWarnings = [];
  const dateLeftBeforeDateJoinedWarnings = [];
  const utrWarnings = [];
  const taxOfficeWarnings = [];
  const joinDateWarnings = [];

  const duplicateCompanyNames = getDuplicates(
    data,
    importedCompanies,
    "company_name",
  );
  const duplicateCompanyNamesWarnings = getDuplicateWarnings(
    duplicateCompanyNames,
    "company_name",
  );

  const duplicateUtrs = getDuplicates(data, importedCompanies, "utr");
  const duplicateUtrWarnings = getDuplicateWarnings(duplicateUtrs, "utr");

  const isFirstPoA = getPoaIndex() === 0;

  importedCompanies.forEach((company) => {
    const companyName = company.company_name;

    if (isJoinDateOutsidePoA(company, "date_join") && companyName) {
      dateOutsidePoAWarnings.push(companyName);
    }

    if (isEndDateBeforeStartDate(company)) {
      dateLeftBeforeDateJoinedWarnings.push(companyName);
    }

    if (!company?.tax_office && companyName) {
      taxOfficeWarnings.push(companyName);
    }

    if (!company?.utr && companyName) {
      utrWarnings.push(companyName);
    }

    if (!isFirstPoA && !company?.date_join && companyName) {
      joinDateWarnings.push(companyName);
    }
  });

  const allCompaniesWarnings = [
    {
      header:
        "The following companies have a join date that is outside the PoA:",
      messages: dateOutsidePoAWarnings,
    },
    {
      header: "The following companies have a date left before date joined:",
      messages: dateLeftBeforeDateJoinedWarnings,
    },
    {
      header: "The following companies have empty Tax Office values:",
      messages: taxOfficeWarnings,
    },
    {
      header: "The following companies have empty UTR values:",
      messages: utrWarnings,
    },
    {
      header: "The following companies have empty Join Dates:",
      messages: joinDateWarnings,
    },
  ];

  const warnings = [
    ...duplicateCompanyNamesWarnings,
    ...duplicateUtrWarnings,
    ...getNonEmptyIssues(allCompaniesWarnings),
  ];
  if (warnings.length > 0) {
    const error = new Error(JSON.stringify(warnings));
    error.type = "warning";
    throw error;
  }
};

const extractValuesFromObject = (list, key) => {
  const values = list.map((item) => {
    const value = item[key];
    return typeof value === "string" && key === "utr"
      ? value.replaceAll("-", "")
      : value;
  });

  return values;
};

const getDuplicates = (oldList, newList, key) => {
  const oldValues = extractValuesFromObject(oldList, key);
  const newValues = extractValuesFromObject(newList, key);

  const newDataDuplicates = newValues.filter(
    (val, index) => newValues.indexOf(val) !== index,
  );
  const commonValues = oldValues.filter((val) => newValues.includes(val));
  const duplicates = [...new Set([...commonValues, ...newDataDuplicates])];

  return duplicates;
};

const getDuplicateWarnings = (duplicates, key) => {
  const warnings = [];
  const headerTitle = headers.find(
    (header) => header.metricId === key,
  )?.display;

  if (duplicates.length > 0) {
    warnings.push({
      header: `The following ${headerTitle} values are duplicates:`,
      messages: [joinWithCommas(duplicates)],
    });
  }

  return warnings;
};

const isJoinDateOutsidePoA = (company, key) => {
  const selectedPoA = getSelectedPeriod();
  const { period_start, period_end } = selectedPoA;

  const date = new Date(company?.[key]);
  return (
    isDateValid(company?.[key]) &&
    (processDateJS(date)?.isBefore(processDateJS(period_start)) ||
      processDateJS(date)?.isAfter(processDateJS(period_end)))
  );
};

const isEndDateBeforeStartDate = (company) => {
  const startDate = new Date(company?.["date_join"]);
  const endDate = new Date(company?.["date_left"]);
  return (
    isDateValid(company?.["date_join"]) &&
    isDateValid(company?.["date_left"]) &&
    processDateJS(endDate)?.isBefore(processDateJS(startDate))
  );
};

export const handleGrpCompTemplateDownload = async ({ data }) => {
  const numOfColumns = columnsConfig.length;
  const headerTitles = headers.map((header) => header.title);
  const { workbook, worksheet } = createExcelDocument(
    templateWorksheetName,
    headerTitles,
  );

  addCompanyRows(worksheet, data);
  addGroupCompaniesTableStyles(worksheet, numOfColumns, data.length);

  disableColumnEditing(worksheet, "F");
  hideColumn(worksheet, "F");

  // enable editing for 300 rows after existing data
  const start = data.length + 2;
  for (let i = start; i < start + 300; i++) {
    for (let j = 1; j <= numOfColumns; j++) {
      enableCellEditingByIndex(worksheet, i, j);
    }
  }

  addValidations({ worksheet, columnsConfig, existingDataLength: data.length });

  addInstructionsWorksheet({
    workbook,
    sheetBuilder: instructionsSheetBuilder,
  });

  await protectWorksheet({ worksheet });

  saveExcelFile(workbook, "Group Companies Import Template");
};

export const handleGrpCompImport = async ({
  file,
  data,
  setData,
  setNewData,
  setIsLoading,
  setWarnings,
  setErrors,
  setIsConfirmModalVisible,
}) => {
  setIsLoading(true);
  let importedCompanies;
  try {
    const worksheet = await getWorksheetFromFile(file, templateWorksheetName);
    const headers = getWorksheetHeaders(worksheet);
    const importedGroupCompaniesDTO = filterNewRows(
      parseExcelFileToJson(worksheet),
    );
    importedCompanies = transformCompanyFormat(importedGroupCompaniesDTO);

    validateHeaders(headers, importedGroupCompaniesDTO);
    validateGroupCompanyRows(importedCompanies);
    checkCompanyForWarnings(data, importedCompanies);
    setData(importedCompanies);
    return true;
  } catch (err) {
    const errorObject = JSON.parse(err.message);
    if (errorObject.missingWorkSheetErrorMessage) {
      setErrors([
        {
          title:
            errorObject.missingWorkSheetErrorMessage ??
            "Error Importing Group Companies",
        },
      ]);
    } else if (err.type === "error") {
      setErrors(errorObject);
    } else if (err.type === "warning") {
      setNewData(importedCompanies);
      setWarnings(errorObject);
      setIsConfirmModalVisible(true);
    }
  }
  setIsLoading(false);
};

export const handleGrpCompConfirmationOnWarnings = ({
  setData,
  reset,
  showNotification,
}) => {
  setData();
  reset();
  showNotification();
};
